import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { environment } from '@environment';
import type {
  SaveWaterExceptionPeriodsRequest,
  SaveWaterPeriodsRequest,
  WaterExceptionPeriod,
  WaterPeriod,
  WaterPricingDimensions,
} from '@models/water-period';
import { Observable } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class CrmWaterPeriodsService {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = `${environment.apiUrl}/api/crm/waters`;

  private periodsUrl(waterId: number): string {
    return `${this.baseUrl}/${waterId}/periods`;
  }

  // Recurring default periods

  getPeriods(waterId: number): Observable<WaterPeriod[]> {
    return this.http.get<WaterPeriod[]>(this.periodsUrl(waterId));
  }

  savePeriods(waterId: number, request: SaveWaterPeriodsRequest): Observable<WaterPeriod[]> {
    return this.http.put<WaterPeriod[]>(this.periodsUrl(waterId), request);
  }

  /**
   * The season and stay-type axes this water offers, for the pricing grids. Water-scoped, unlike the
   * global `period-types` / `period-times` code lists: the API derives both from the water's periods and
   * its week/midweek/weekend flags, and rejects/drops pricing cells for dimensions outside these sets.
   */
  getPricingDimensions(waterId: number): Observable<WaterPricingDimensions> {
    return this.http.get<WaterPricingDimensions>(`${this.baseUrl}/${waterId}/pricing-dimensions`);
  }

  // Year-specific exception periods

  getExceptionYears(waterId: number): Observable<number[]> {
    return this.http.get<number[]>(`${this.periodsUrl(waterId)}/exception-years`);
  }

  getExceptionPeriods(waterId: number, year: number): Observable<WaterExceptionPeriod[]> {
    const params = new HttpParams().set('year', year);
    return this.http.get<WaterExceptionPeriod[]>(`${this.periodsUrl(waterId)}/exceptions`, { params });
  }

  saveExceptionPeriods(
    waterId: number,
    year: number,
    request: SaveWaterExceptionPeriodsRequest,
  ): Observable<WaterExceptionPeriod[]> {
    const params = new HttpParams().set('year', year);
    return this.http.put<WaterExceptionPeriod[]>(`${this.periodsUrl(waterId)}/exceptions`, request, { params });
  }
}
